const LEADERBOARD_CHANNEL = 'ergshoot-leaderboard-sync';
const LEADERBOARD_STORAGE_KEY = 'ergshoot:leaderboard-sync';
const TAB_ID = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

export interface LeaderboardUpdateEvent {
  type: 'score-updated';
  workoutId: string;
  timestamp: number;
  sourceId: string;
}

function isLeaderboardUpdateEvent(value: unknown): value is LeaderboardUpdateEvent {
  if (!value || typeof value !== 'object') return false;

  const event = value as Partial<LeaderboardUpdateEvent>;
  return (
    event.type === 'score-updated' &&
    typeof event.workoutId === 'string' &&
    event.workoutId.length > 0 &&
    typeof event.timestamp === 'number' &&
    typeof event.sourceId === 'string'
  );
}

export function publishLeaderboardUpdate(workoutId: string): void {
  if (typeof window === 'undefined') return;

  const event: LeaderboardUpdateEvent = {
    type: 'score-updated',
    workoutId,
    timestamp: Date.now(),
    sourceId: TAB_ID,
  };

  if (typeof window.BroadcastChannel === 'function') {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new window.BroadcastChannel(LEADERBOARD_CHANNEL);
      channel.postMessage(event);
      return;
    } catch {
      // Fall back to storage when channel access is blocked by browser policy.
    } finally {
      try {
        channel?.close();
      } catch {
        // Cleanup is best-effort for the same browser-policy constraints.
      }
    }
  }

  try {
    window.localStorage.setItem(LEADERBOARD_STORAGE_KEY, JSON.stringify(event));
    window.localStorage.removeItem(LEADERBOARD_STORAGE_KEY);
  } catch {
    // Cross-tab updates are best-effort and must not interrupt a saved score flow.
  }
}

export function subscribeToLeaderboardUpdates(
  listener: (event: LeaderboardUpdateEvent) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  if (typeof window.BroadcastChannel === 'function') {
    try {
      const channel = new window.BroadcastChannel(LEADERBOARD_CHANNEL);
      channel.onmessage = ({ data }) => {
        if (isLeaderboardUpdateEvent(data) && data.sourceId !== TAB_ID) listener(data);
      };
      return () => {
        try {
          channel.close();
        } catch {
          // Cleanup is best-effort when browser policy blocks channel access.
        }
      };
    } catch {
      // Fall back to storage when channel access is blocked by browser policy.
    }
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== LEADERBOARD_STORAGE_KEY || !event.newValue) return;

    try {
      const data: unknown = JSON.parse(event.newValue);
      if (isLeaderboardUpdateEvent(data) && data.sourceId !== TAB_ID) listener(data);
    } catch {
      // Ignore malformed cross-tab messages.
    }
  };

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
}
